import { getInitials } from '@sdk/components/avatar/utils';
import { isFeatureEnabled } from '@sdk/utils/isFeatureEnabled';

import sortPickerItemsAlphabetically from '@angular/dashboard/manage/helpers/sortPickerItemsAlphabetically';

import {
  EMPLOYEE_HEADER_HEIGHT,
  FILTERS_BAR_HEIGHT,
  TIMEZONE_BANNER_HEIGHT,
} from '../../constants';
import config from '../../../config';
import { DEFAULT_USERS_COLUMNS, USERS_COLUMN_DESCRIPTIONS } from '../../../users/constants';
import { SUBSCRIPTION_LEVELS } from '../../../account/constants';
import ReportTableColumns from '../../../reports/services/ReportTableColumns';

import deleteUrl from './delete.html';
import modalInviteSuccessUrl from './modal-invite-success.html';
import modalConfirmInviteUrl from './modal-confirm-invite.html';
import usersListTableUrl from './users-list-table.html';
import listUrl from './manage-user-list.html';
import verifyToInviteTooltipUrl from './verify-to-invite-tooltip.html';
import USERS_UI_SCHEMA from './usersUISchema';

const USERS_COLUMNS_PROPERTY = 'usersColumns';
class ManageUserListCtrl {
  usersListTableUrl = usersListTableUrl;
  listUrl = listUrl;
  verifyToInviteTooltipUrl = verifyToInviteTooltipUrl;
  selfInvitedUsers = [];
  listInitialized = false;
  selectedUsersToMerge = [];
  availableUsersToMerge = [];

  constructor(
    Restangular,
    Authentication,
    Users,
    Utils,
    UserUtils,
    PersonaCollection,
    AlertService,
    ConversationService,
    ChooseService,
    EmployeeSearchService,
    SlingApi,
    TabsControl,
    IntegrationsEducation,
    extendUserModel,
    displayNumberFilter,
    Education,
    sdk,
    $rootScope,
    $scope,
    $uibModal,
    $state,
    $sce,
    $timeout
  ) {
    'ngInject';

    Object.assign(this, {
      Restangular,
      Authentication,
      Users,
      Utils,
      UserUtils,
      PersonaCollection,
      AlertService,
      ConversationService,
      ChooseService,
      EmployeeSearchService,
      SlingApi,
      TabsControl,
      extendUserModel,
      displayNumberFilter,
      Education,
      $rootScope,
      $scope,
      $state,
      $uibModal,
      $sce,
      $timeout,
    });

    this.USER_STATUSES = sdk('constants').USER_STATUSES;
    this.exportUrl = $sce.trustAsResourceUrl(`${Authentication.baseUrl}users/export`);
    this.manageMode = Authentication.user.isManager;
    this.search = '';
    this.positions = PersonaCollection.positions;
    this.locations = PersonaCollection.locations;
    this.groups = PersonaCollection.groups;
    this.canManageUser = UserUtils.canManageUser;
    this.filters = {};
    this.FREE_USER_LIMIT_ENABLED = isFeatureEnabled(config.FREE_USER_LIMIT_ENABLED);

    this.setUserGroups();
    this._deselectAll();

    this.initTable();

    if (this.$state.params.showInviteTooltip === 'true') {
      this.timeoutId = this.$timeout(() => this.showInviteEducationTooltip(), 2000);
    }

    const unsubscribe = PersonaCollection.onChange(() => this.refreshList(this.filters));
    $scope.$on('$destroy', () => {
      unsubscribe();
      this.tabsControl?.destroy();
      this.$timeout.cancel(this.timeoutId);
    });

    $scope.$watch('ctrl.users.length', () => {
      this.updateShowInviteAll();
    });

    setTimeout(
      () => IntegrationsEducation.showStep('toast', IntegrationsEducation.stepTypes.employees),
      1000
    );
  }

  onFiltersChange(filters) {
    if (this.listInitialized) {
      this.refreshList(filters);
    }
  }

  initTable() {
    this._filtersOpened = this.Authentication.user.clientcache?.usersList?.filtersOpened;
    this.initUsersTabs();
    this.schema = USERS_UI_SCHEMA;
    this.columnDescriptions = USERS_COLUMN_DESCRIPTIONS;
    this.initTableColumns();
  }

  async initUsersTabs() {
    await this.initUsersList();
    this.setTabs();
    this.listInitialized = true;
  }

  get filtersOpened() {
    return this._filtersOpened;
  }

  set filtersOpened(val) {
    this._filtersOpened = val;
    if (!this.Authentication.user.clientcache.usersList) {
      this.Authentication.user.clientcache.usersList = {};
    }

    this.Authentication.user.clientcache.usersList.filtersOpened = val;
    this.Authentication.syncClientCache();
  }

  get stickyBarOffset() {
    return (
      this.$rootScope.navbarHeight +
      (this.$rootScope.timezoneDiscrepancyShown ? TIMEZONE_BANNER_HEIGHT : 0)
    );
  }

  initTableColumns() {
    if (!(USERS_COLUMNS_PROPERTY in this.Authentication.user.settings.clientFlags)) {
      this.Authentication.user.settings.clientFlags[USERS_COLUMNS_PROPERTY] = DEFAULT_USERS_COLUMNS;
      this.Authentication.syncSettings();
    }

    this.columns = new ReportTableColumns(this.Authentication, USERS_COLUMNS_PROPERTY);
    this.columns.reset();
  }

  setTabs() {
    this.tabs = this.Authentication.user.isManager
      ? [
          { value: 'employees', label: 'Employees' },
          {
            value: 'requests',
            label: 'Join requests',
            getBadgesCount: () => this.selfInvitedUsers?.length,
          },
          {
            value: 'deactivated',
            label: 'Deactivated',
            show: () => this.managableUsers?.some((u) => !u.active),
          },
        ]
      : [{ value: 'users', label: '' }];

    this.tabsControl = new this.TabsControl(this.tabs, this.tabs[0].value);
  }

  columnsChanged() {
    this.columns.changed();
    this.$rootScope.$broadcast('vsRepeatTrigger');
  }

  changeTab(value) {
    if (value === this.activeTab) {
      return;
    }

    const tab = this.tabs.find((t) => t.value === value);
    this.tabsControl.changeTab(tab);
  }

  setUserGroups() {
    const instance = this;
    this.userGroups = this.Authentication.user.isManager
      ? [
          {
            name: 'regularUsers',
            get hidden() {
              return instance.activeTab !== 'employees';
            },
          },
          {
            name: 'notInvitedUsers',
            get hidden() {
              return instance.activeTab !== 'employees';
            },
          },
          {
            name: 'invitedUsers',
            get hidden() {
              return instance.activeTab !== 'employees';
            },
          },
          {
            name: 'deactivatedUsers',
            get hidden() {
              return instance.activeTab !== 'deactivated';
            },
          },
          {
            name: 'selfInvitedUsers',
            get hidden() {
              return instance.activeTab !== 'requests';
            },
          },
        ]
      : [{ name: 'users' }];
  }

  get search() {
    return this._search;
  }

  set search(value) {
    this._search = value;

    this.Utils.debounce(this.onSearchChange, 300);
  }

  updateUserCounters = () => {
    const { employeesCount, joinRequestsCount, deactivatedCount } =
      this.EmployeeSearchService.onActiveEmployeesSearch(
        {
          regularUsers: this.regularUsers,
          notInvitedUsers: this.notInvitedUsers,
          invitedUsers: this.invitedUsers,
          deactivatedUsers: this.deactivatedUsers,
          selfInvitedUsers: this.selfInvitedUsers,
        },
        this.search,
        this.activeTab
      );

    this.employeesCount = employeesCount;
    this.joinRequestsCount = joinRequestsCount;
    this.deactivatedCount = deactivatedCount;
  };

  onSearchChange = () => {
    this.updateUserCounters();
    this.$scope.$broadcast('vsRepeatTrigger');
  };

  get activeTab() {
    return this.tabsControl?.activeTab.value;
  }

  // eslint-disable-next-line complexity
  get activeTip() {
    const currentUser = this.Authentication.user;
    if (!this.manageMode) {
      return null;
    }

    const employeesTabActive = this.activeTab === 'employees';
    const TOOLTIPS_DICT = [
      { key: 'VERIFY', isShown: () => currentUser.status === 'unverified' },
      {
        key: 'IMPORT',
        isShown: () =>
          employeesTabActive &&
          !currentUser.settings.clientFlags.importUsersTipShown &&
          this.PersonaCollection.users.length === 1,
      },
      {
        key: 'MISSING_DETAILS',
        isShown: () =>
          employeesTabActive &&
          this.missingDetailsUsers.length &&
          !currentUser.settings.clientFlags.missingDetailsUsersTipDismissed,
      },
      {
        key: 'INVITE_ALL',
        isShown: () => employeesTabActive && this.showInviteAll && this.uninvitedUsers.length,
      },
      {
        key: 'IMPORT',
        isShown: () => employeesTabActive && !currentUser.settings.clientFlags.importUsersTipShown,
      },
    ];

    return TOOLTIPS_DICT.find((tooltip) => tooltip.isShown())?.key;
  }

  _deselectAll() {
    this.selectedItemIds = {};
    this.userGroups.forEach((userGroup) => {
      this[`selectedItemIds-${userGroup.name}`] = Object.create(null);
    });
  }

  onSelectionChange(changes = {}) {
    this.selectedItemIds = Object.assign(this.selectedItemIds, changes);
  }

  get isSomethingSelected() {
    return this.userGroups.some((g) => this[`isSomethingSelected-${g.name}`]);
  }

  deselectGroup(name) {
    const visibleItems = this[`visibleItemsDict-${name}`];
    Object.values(visibleItems).forEach(({ id }) => {
      delete this.selectedItemIds[id];
    });

    this[`isSomethingSelected-${name}`] = false;
    this[`selectedItemIds-${name}`] = Object.create(null);
  }

  get visibleItemsDict() {
    return this.userGroups
      .filter((group) => !group.hidden)
      .reduce((acc, userGroup) => ({ ...acc, ...this[`visibleItemsDict-${userGroup.name}`] }), {});
  }

  get idsForExport() {
    return Object.keys(this.visibleItemsDict).map(Number).join(',');
  }

  get activeSelectedUsers() {
    const { visibleItemsDict } = this;
    return Object.keys(this.selectedItemIds)
      .filter(
        (id) =>
          this.selectedItemIds[id] &&
          id in visibleItemsDict &&
          this.PersonaCollection.personasDict[id] &&
          this.PersonaCollection.personasDict[id].active
      )
      .map((id) => this.PersonaCollection.personasDict[id]);
  }

  get selectedUsersForNewConversation() {
    return this.activeSelectedUsers.filter((u) => !u.isMe);
  }

  get deactivatedSelectedUsers() {
    const { visibleItemsDict } = this;
    return Object.keys(this.selectedItemIds)
      .filter(
        (id) =>
          this.selectedItemIds[id] &&
          id in visibleItemsDict &&
          this.PersonaCollection.personasDict[id] &&
          !this.PersonaCollection.personasDict[id].active
      )
      .map((id) => this.PersonaCollection.personasDict[id]);
  }

  get uninvitedUsers() {
    return this.PersonaCollection.users.filter((user) => user.status === 'uninvited');
  }

  get missingDetailsUsers() {
    return this.PersonaCollection.users.filter((user) => user.missingInviteDetails);
  }

  get allSelectedUsers() {
    return this.activeSelectedUsers.concat(this.deactivatedSelectedUsers);
  }

  get canExecuteMultiUserMerge() {
    return this.allSelectedUsers.length <= 20;
  }

  get selectedUsersToInvite() {
    return this.activeSelectedUsers.filter(
      (u) => u.status !== this.USER_STATUSES.VERIFIED && !u.missingInviteDetails
    );
  }

  get selectedUsersToDelete() {
    return this.allSelectedUsers.filter((user) => this.UserUtils.canManageUser(user));
  }

  get selectedUsersToDeactivate() {
    return this.activeSelectedUsers.filter((user) => this.UserUtils.canManageUser(user));
  }

  get selectedUsersToActivate() {
    return this.deactivatedSelectedUsers.filter((user) => this.UserUtils.canManageUser(user));
  }

  get multiselectOffset() {
    return (
      this.$rootScope.navbarHeight +
      EMPLOYEE_HEADER_HEIGHT +
      (this.$rootScope.timezoneDiscrepancyShown ? TIMEZONE_BANNER_HEIGHT : 0) +
      (this.filtersOpened ? FILTERS_BAR_HEIGHT : 0)
    );
  }

  get filtersEmpty() {
    return Object.values(this.filters).every((filter) => !filter.length);
  }

  async initUsersList() {
    if (this.Authentication.user.isManager) {
      const orgId = this.Authentication.user.org.id;
      const selfInvitedUsers = await this.SlingApi.getSelfInvitedUsers(orgId);
      this.selfInvitedUsers = selfInvitedUsers.map(this.extendSelfInvitedUser);
    }

    this.refreshList(this.filters);
  }

  getTabTitle() {
    const employeeCountDict = {
      employees: {
        number: this.Authentication.user.isManager
          ? this.getCount(this.regularUsers) +
            this.getCount(this.invitedUsers) +
            this.getCount(this.notInvitedUsers)
          : this.users.length,
        title: 'Employee',
      },
      deactivated: {
        number: this.getCount(this.deactivatedUsers),
        title: 'Deactivated employee',
      },
      requests: {
        number: this.getCount(this.selfInvitedUsers),
        title: 'Request',
      },
    };

    const titleGroup = employeeCountDict[this.activeTab] || employeeCountDict.employees;
    const suffix = titleGroup.number !== 1 ? 's' : '';

    return `${titleGroup.number} ${titleGroup.title}${suffix}`;
  }

  getCount(group) {
    return group?.length || 0;
  }

  extendSelfInvitedUser = (user) =>
    this.extendUserModel({
      ...user,
      active: true,
      avatar: null,
      status: 'requested_invite',
    });

  async acceptInviteRequest(user) {
    const orgId = this.Authentication.user.org.id;
    const acceptedUser = await this.SlingApi.acceptSelfInvite(orgId, user.id);
    this.PersonaCollection.addUser(acceptedUser);
    this.selfInvitedUsers = this.selfInvitedUsers.filter((u) => u.id !== user.id);
  }

  async rejectInviteRequest(user) {
    const orgId = this.Authentication.user.org.id;
    await this.SlingApi.rejectSelfInvite(orgId, user.id);
    this.selfInvitedUsers = this.selfInvitedUsers.filter((u) => u.id !== user.id);
  }

  updateShowInviteAll() {
    if (!this.users) {
      return;
    }

    this.showInviteAll = this.users.some(
      (user) =>
        user.active &&
        !user.missingInviteDetails &&
        (user.status === 'uninvited' || user.status === 'invited')
    );
  }

  deleteUsers(usersArg) {
    const users = usersArg.filter((u) => !u.isMe);
    if (!users.length) {
      return false;
    }

    this._deleteOrDeactivate(users, async (params) => {
      const data = users.map((user) => ({
        id: user.id,
        deleted: true,
      }));
      await this.Restangular.all('users').customPUT(data, null, params);
      this._deselectAll();
    });
  }

  deactivateUsers(usersArg) {
    const users = usersArg.filter((u) => !u.isMe);
    if (!users.length) {
      return false;
    }

    this._deleteOrDeactivate(
      users,
      async (params) => {
        const data = users.map((user) => ({
          id: user.id,
          active: false,
        }));
        await this.Restangular.all('users').customPUT(data, null, params);
        this._deselectAll();
      },
      'deactivate'
    );
  }

  mapUserToMergeModalItem = (user) => ({
    id: user.id,
    label: `${user.name} ${user.lastname}`,
    sub: user.positions?.[0]?.name,
    subColor: user.positions?.[0]?.color,
    extra: user.locations?.[0]?.name,
    icon: user.avatar,
    iconText: getInitials(user),
  });

  mergeUsers(usersArg) {
    const users = usersArg.filter((u) => !u.isMe);

    if (!users.length) {
      return false;
    }

    if (users.length > 1 && !this.canExecuteMultiUserMerge) {
      return false;
    }

    this.selectedUsersToMerge = users.map(this.mapUserToMergeModalItem);
    this.availableUsersToMerge = sortPickerItemsAlphabetically(
      this.users.filter((user) => user.active).map(this.mapUserToMergeModalItem)
    );
    this.openUserMergeModal = true;
  }

  onUserMergeModalClose() {
    this.openUserMergeModal = false;
  }

  onUserMergeModalSubmit() {
    this.openUserMergeModal = false;
    this._deselectAll();
  }

  async activateUsers(users) {
    if (!users.length) {
      return false;
    }

    const data = users.map((user) => ({
      id: user.id,
      active: true,
    }));

    await this.Restangular.all('users').customPUT(data, null);
    this._deselectAll();
  }

  openStatusInfo(user) {
    let question = '';
    let buttonTitle = '';

    if (user.status === 'uninvited') {
      question = `${user.fullname} has not been invited to join ${this.Authentication.user.org.name} on Sling yet. Would you like to send ${user.fullname} an invite now?`;
      buttonTitle = 'Send invite';
    }

    if (user.status === 'invited') {
      question = `${user.fullname} has been invited to join ${this.Authentication.user.org.name} on Sling, but has not created an account yet. Would you like to resend the invite?`;
      buttonTitle = 'Resend invite';
    }

    if (user.isUnverified) {
      question = `${user.fullname} has an unverified email address ${user.email}. Would you like to resend the verification email?`;
      buttonTitle = 'Resend verification';
    }

    this.ChooseService.ask(question, [
      {
        button: buttonTitle,
        class: 'btn-primary btn-block btn-lg',
        handler: () => {
          this.Users.all('invite')
            .post([{ id: user.id }])
            .then(() => {
              if (user.status === 'uninvited') {
                user.status = 'invited';
                this.extendUserModel(user);
                this.updateShowInviteAll();
                if (this.Authentication.user.isManager) {
                  this.sortUsersByGroup();
                }
              }
            });
        },
      },
      {
        button: 'Cancel',
        class: 'btn-cancel btn-block btn-lg',
      },
    ]);
  }

  async inviteUsers(onlySelected = false) {
    if (onlySelected && !this.selectedUsersToInvite.length) {
      return;
    }

    let payload;
    let alreadyInvitedExist = false;
    if (onlySelected) {
      payload = this.selectedUsersToInvite.map((user) => ({ id: user.id }));

      if (!payload.length) {
        return;
      }

      alreadyInvitedExist = this.selectedUsersToInvite.some((user) => user.status === 'invited');
    } else {
      alreadyInvitedExist = this.regularUsers.some((user) => user.status === 'invited');
    }

    const includeInvited = await this.$uibModal.open({
      templateUrl: modalConfirmInviteUrl,
      size: '620',
      controllerAs: 'ctrl',
      controller() {
        this.showIncludeInvitedSwitch = !onlySelected && alreadyInvitedExist;
      },
    }).result;

    const invitedUsers = await this.Restangular.all('users')
      .all('invite')
      .post(payload, { includeInvited });
    const invitedUsersDict = invitedUsers.reduce((acc, user) => ((acc[user.id] = true), acc), {});

    const users = onlySelected ? this.selectedUsersToInvite : this.regularUsers;
    let uninvitedDueToMissingDetailsCount = onlySelected
      ? this.activeSelectedUsers.filter(
          (u) => u.status !== this.USER_STATUSES.VERIFIED && u.missingInviteDetails
        ).length
      : 0;

    users.forEach((user) => {
      if (user.missingInviteDetails && !onlySelected) {
        uninvitedDueToMissingDetailsCount++;
      }

      if (invitedUsersDict[user.id]) {
        user.status = 'invited';
        this.extendUserModel(user);
      }
    });

    this.updateShowInviteAll();

    if (onlySelected) {
      this._deselectAll();
    }

    this.showInviteSuccessModal(invitedUsers.length, uninvitedDueToMissingDetailsCount);
  }

  getOrInviteUrl() {
    this.$uibModal.open({
      size: '620',
      template:
        '<org-invite-url-modal modal-instance="ctrl.$uibModalInstance"></org-invite-url-modal>',
      controllerAs: 'ctrl',
      controller($uibModalInstance) {
        'ngInject';

        this.$uibModalInstance = $uibModalInstance;
      },
    });
  }

  showInviteSuccessModal(invitedCount, cannotInviteCount) {
    this.$uibModal.open({
      templateUrl: modalInviteSuccessUrl,
      size: cannotInviteCount ? '620' : '400',
      controllerAs: 'ctrl',
      controller() {
        this.invitedCount = invitedCount;
        this.cannotInviteCount = cannotInviteCount;
      },
    });
  }

  get canStartConversation() {
    return (
      !this.Authentication.user.orgSettings.messagesFeatureDisabled &&
      (this.Authentication.user.isManager ||
        this.Authentication.user.orgSettings.usersCanStartConversation !== 'NONE')
    );
  }

  get canAddEmployee() {
    const isOnTrial = this.Authentication.user.org.isOnTrial;
    const userLimitEnabled = this.Authentication.userCountLimits.total.enabled; //server feature flag
    const hasToastIntegration = this.Authentication.user.org.hasToastIntegration;
    const isFreeAccount =
      this.Authentication.user.org.subscriptionLevel === SUBSCRIPTION_LEVELS.FREE;

    if (userLimitEnabled && this.FREE_USER_LIMIT_ENABLED) {
      if (isOnTrial || (!hasToastIntegration && isFreeAccount)) {
        return (
          this.PersonaCollection.users.length < this.Authentication.userCountLimits.total.limit
        );
      }
    }
    return true;
  }

  get multiselectEnabled() {
    return this.Authentication.user.isManager || this.canStartConversation;
  }

  startConversation() {
    if (!this.selectedUsersForNewConversation.length || this.isCreateConversationDisabled()) {
      return;
    }

    if (
      !this.Authentication.isOrgFeatureEnabled('privateMessages') &&
      !this.Authentication.isOrgFeatureEnabled('groupMessages')
    ) {
      this.$state.go('messages', {
        path: 'preview',
      });
      return;
    }

    const users = this.selectedUsersForNewConversation;

    if (users.length) {
      const ids = users.map(({ id }) => id);
      this.ConversationService.openConversation(ids);

      return true;
    }
  }

  isCreateConversationDisabled() {
    return (
      !this.selectedUsersForNewConversation.length ||
      (!this.Authentication.user.isManager &&
        this.Authentication.user.orgSettings.usersCanStartConversation === 'PRIVATE_ONLY' &&
        this.selectedUsersForNewConversation.length > 1)
    );
  }

  getTooltipForStartConversation() {
    if (!this.isCreateConversationDisabled()) {
      return '';
    }

    if (
      !this.Authentication.user.isManager &&
      this.Authentication.user.orgSettings.usersCanStartConversation === 'PRIVATE_ONLY' &&
      this.selectedUsersForNewConversation.length > 1
    ) {
      return 'You can only create conversations with one employee';
    }
  }

  async addToGroup(group) {
    const selected = this.allSelectedUsers;

    let someoneAdded = false;
    selected.forEach((user) => {
      if (!group.members.find((m) => m.id === user.id)) {
        group.members.push({ id: user.id });
        someoneAdded = true;
      }
    });

    if (someoneAdded) {
      await group.put();
      this._deselectAll();
    }
  }

  dismissImportUsersTipShown() {
    this.Authentication.user.settings.clientFlags.importUsersTipShown = true;
    this.Authentication.syncSettings();
  }

  dismissMissingDetailsTipShown() {
    this.Authentication.user.settings.clientFlags.missingDetailsUsersTipDismissed = true;
    this.Authentication.syncSettings();
  }

  refreshList(filters) {
    this.setManagableUsers();
    this.users = this._getFilteredUsers(filters);

    if (this.Authentication.user.isManager) {
      this.sortUsersByGroup();
    }
  }

  sortUsersByGroup() {
    const currentUser = this.Authentication.user;
    this.deactivatedUsers = this.users.filter((u) => !u.active);
    this.regularUsers = this.users.filter(
      (u) =>
        (u.active && u.status === this.USER_STATUSES.VERIFIED) ||
        (u.id === currentUser.id && currentUser.isOrgCreator)
    );
    this.invitedUsers = this.users.filter(
      (u) =>
        u.active &&
        (u.status === this.USER_STATUSES.INVITED || u.status === this.USER_STATUSES.WRONG_EMAIL)
    );
    this.notInvitedUsers = this.users.filter(
      (u) => u.active && u.status === this.USER_STATUSES.UNINVITED
    );
  }

  resendVerification() {
    this.UserUtils.resendVerification();
    this.AlertService.info('Verification email has been sent');
  }

  _getFilteredUsers(filters) {
    const users = this.managableUsers;
    if (!users?.length || !filters) {
      return users;
    }

    return users.filter((user) => this._isShownByFilters(user, filters));
  }

  _isShownByFilters(user, filters) {
    return (
      this._isShownByRoleFilter(user, filters) &&
      this._isShownByStatusFilter(user, filters) &&
      this._isShownByPositionFilter(user, filters) &&
      this._isShownByLocationFilter(user, filters) &&
      this._isShownByGroupFilter(user, filters)
    );
  }

  _isShownByPositionFilter(user, filters) {
    return (
      !filters.positionIds?.length ||
      (!user.positionIds.length && filters.positionIds.includes('noPositions')) ||
      filters.positionIds?.some((id) => user.positionIds.includes(id))
    );
  }

  _isShownByLocationFilter(user, filters) {
    return (
      !filters.locationIds?.length ||
      (!user.locationIds.length && filters.locationIds.includes('noLocations')) ||
      filters.locationIds?.some((id) => user.locationIds.includes(id))
    );
  }

  _isShownByGroupFilter(user, filters) {
    return (
      !filters.groupIds?.length ||
      (!user.groups.length && filters.groupIds.includes('noGroups')) ||
      filters.groupIds?.some((id) => user.groupIds.includes(id))
    );
  }

  _isShownByRoleFilter(user, filters) {
    return !filters.roles?.length || filters.roles.includes(user.role);
  }

  _isShownByStatusFilter(user, filters) {
    if (!filters.statuses?.length || !user.active) {
      return true;
    }

    const statusFilters = filters.statuses;
    const shownByInvited = statusFilters.includes('invited') && user.status === 'invited';
    const shownByUninvited =
      statusFilters.includes('notInvited') && user.status === 'uninvited' && user.email;
    const shownByNoEmail = this._isShownByNoEmailFilter(user, statusFilters);

    return Boolean(
      this._isShownByVerifiedFilter(user, filters) ||
        shownByInvited ||
        shownByUninvited ||
        shownByNoEmail
    );
  }

  _isShownByNoEmailFilter(user, statusFilters) {
    return (
      statusFilters.includes('notInvitedNoEmail') && user.status === 'uninvited' && !user.email
    );
  }

  _isShownByVerifiedFilter(user, filters) {
    const statusFilters = filters.statuses;
    const shownByVerified =
      statusFilters.includes('joined') && user.status === this.USER_STATUSES.VERIFIED;
    const shownByUnverified = statusFilters.includes('unverified') && user.isUnverified;

    return shownByVerified || shownByUnverified;
  }

  setManagableUsers() {
    this.managableUsers = this.Authentication.user.isManager
      ? this._getManagableUsers()
      : this.PersonaCollection.visibleUsers;
    this.hasImportedUsers = this.PersonaCollection.hasImportedUsers;
  }

  _getManagableUsers() {
    let managableUsers = this.PersonaCollection.allUsers;
    if (this.displayOnlyManagable) {
      managableUsers = managableUsers.filter((user) => this.UserUtils.canManageUser(user));
    }
    return managableUsers;
  }

  _deleteOrDeactivate(users, onSubmit, mode = 'delete') {
    const parentController = this;
    this.$uibModal
      .open({
        templateUrl: deleteUrl,
        controllerAs: 'ctrl',
        controller($uibModalInstance) {
          'ngInject';

          const modalthis = this;
          modalthis.userNames = parentController.Utils.addJoin(users.map((u) => u.fullname).sort());
          const userIds = users.map((u) => u.id);
          modalthis.users = parentController.regularUsers.filter((u) => !userIds.includes(u.id));

          modalthis.shifts = 'delete';
          modalthis.to = undefined;
          modalthis.tasks = 'delete';
          modalthis.tasksTo = undefined;

          modalthis.mode = mode;
          modalthis.oldTasksUsed = parentController.Authentication.user.org.oldTasksUsed;

          modalthis.submit = function submit() {
            const promise = onSubmit({
              shifts: modalthis.shifts,
              to: modalthis.to?.[0]?.id ?? undefined,
              tasks: modalthis.tasks,
              tasksTo: modalthis.tasksTo?.[0]?.id ?? undefined,
            });

            promise.then(() => {
              $uibModalInstance.close();
            });

            return promise;
          };
        },
      })
      .result.ignoreCancel();
  }

  shouldHideColumn(column) {
    const conditions = {
      positions: () => this.activeTab === 'requests',
      locations: () => this.activeTab === 'requests',
      groups: () => this.activeTab === 'requests',
      employeeId: () => !this.manageMode || this.activeTab === 'requests',
      email: () => !this.manageMode && !this.Authentication.user.orgSettings.usersSeePersonalData,
      phone: () =>
        this.activeTab === 'requests' ||
        (!this.manageMode && !this.Authentication.user.orgSettings.usersSeePersonalData),
      status: () => !this.manageMode,
    };

    return conditions[column.property]?.();
  }

  canDataColumnBeVisible(column) {
    if (!this.columns) {
      return true;
    }

    const col = this.columns.reportColumns.find((c) => c.property === column);
    return !col || !this.shouldHideColumn(col);
  }

  getDataForExport() {
    const columns = this.columns.reportColumns.filter((c) => c.active && !this.shouldHideColumn(c));
    const data = this.userGroups.reduce((acc, userGroup) => {
      if (!userGroup.hidden) {
        this[userGroup.name].forEach((u) => acc.push(u));
      }

      return acc;
    }, []);

    columns.unshift({ property: 'name' });
    const dataForExport = {
      header: columns.map((c) => USERS_COLUMN_DESCRIPTIONS[c.property]?.toUpperCase()),
      body: data.map((user) => columns.map((c) => this._mapItemField(user, c.property))),
    };

    return dataForExport;
  }

  _mapItemField(user, property) {
    const getGroupNames = (groups) => groups.map(({ name }) => name).join(', ');
    const gettersDict = {
      name: () => user.fullname,
      employeeId: () => user.employeeId,
      email: () => user.email,
      phone: () => (user.phone ? `${user.countryCode} ${user.phone}` : ''),
      status: () => user.statusText,
      locations: () => getGroupNames(user.locations || []),
      positions: () => getGroupNames(user.positions || []),
      groups: () => getGroupNames(user.groups || []),
    };

    return gettersDict[property] ? gettersDict[property]() : '';
  }

  showInviteEducationTooltip() {
    this.Education.show({
      name: 'EDUCATION_INVITE_EMPLOYEES',
      content: `
        <div>
          Add your team to be able to build schedules. Import employees from a file, invite them individually, or share a link so they can join your company account.
        </div>
      `,
      selector: '#education-invite-employees',
      chevron: 'top-right',
    });
  }
}

export default ManageUserListCtrl;
